<template>
	<div class="w-100 h-100 d-flex ao-grouped-list">
		<v-expansion-panels
			variant="accordion"
			:model-value="expandedGroup"
			@update:model-value="expandedGroup = $event"
		>
			<template
				v-for="(group, index) in items"
				:key="group[groupUniq]"
			>
				<v-expansion-panel
					:class="{ 'mt-2': index !== 0 }"
					:value="group[groupUniq]"
				>
					<template v-slot:title>
						<div
							class="w-100 h-100 d-flex align-center justify-space-between ml-2 ao-grouped-list__group"
							:class="{
								'ao-grouped-list__group--active': activeGroup === group[groupUniq]
							}"
						>
							<div class="d-flex align-center">
								<ao-icon
									iconName="folder"
									class="text-textMuted"
								/>

								<p class="text-text90 font-weight-bold ao-pw-regular ml-2">{{ group[groupTitle] }}</p>
							</div>

							<div>
								<p class="ao-grouped-list__group-aggregation text-textSecondary font-weight-bold ao-p4-medium">
									{{ group[groupAggregation] }}
								</p>

								<div
									v-if="hasGroupActions"
									class="ao-grouped-list__group-actions"
								>
									<v-menu @update:modelValue="onMenuModelValueUpdate(group[groupUniq], $event)">
										<template v-slot:activator="{ props }">
											<ao-icon-btn
												v-bind="props"
												variant="tonal"
												color="primary"
												iconName="dotsHorizontal"
												size="22"
												class="ml-2"
											/>
										</template>

										<v-card>
											<v-list
												dense
												class="py-2"
											>
												<template
													v-for="(action, index) in groupActions"
													:key="index"
												>
													<template v-if="action.actions">
														<v-menu location="end">
															<template v-slot:activator="{ props }">
																<v-list-item
																	v-bind="props"
																	density="compact"
																	height="32"
																	max-height="32"
																	min-height="32"
																>
																	<template v-slot:prepend>
																		<ao-icon
																			class="ao-text-800"
																			size="16"
																			:iconName="action.icon"
																		/>
																	</template>

																	<v-list-item-title class="ml-2 text-text900 font-weight-bold">
																		{{ action.title }}
																	</v-list-item-title>
																</v-list-item>
															</template>

															<v-card>
																<v-list>
																	<v-list-item
																		v-for="(childAction, index) in action.actions"
																		:key="index"
																		:value="childAction"
																		density="compact"
																		height="32"
																		max-height="32"
																		min-height="32"
																		@click="onGroupAction([action.name, childAction.name], group[groupUniq])"
																	>
																		<template v-slot:prepend>
																			<ao-icon
																				class="ao-text-800"
																				size="16"
																				:iconName="childAction.icon"
																			/>
																		</template>

																		<v-list-item-title class="ml-2 text-text900 font-weight-bold">
																			{{ childAction.title }}
																		</v-list-item-title>
																	</v-list-item>
																</v-list>
															</v-card>
														</v-menu>
													</template>

													<template v-else>
														<v-list-item
															density="compact"
															height="32"
															max-height="32"
															min-height="32"
															@click="onGroupAction(action.name, group[groupUniq])"
														>
															<template v-slot:prepend>
																<ao-icon
																	class="ao-text-800"
																	size="16"
																	:iconName="action.icon"
																/>
															</template>

															<v-list-item-title class="ml-2 text-text900 font-weight-bold">
																{{ action.title }}
															</v-list-item-title>
														</v-list-item>
													</template>
												</template>
											</v-list>
										</v-card>
									</v-menu>
								</div>
							</div>
						</div>
					</template>
					<template v-slot:text>
						<TransitionGroup name="list">
							<div
								v-for="item in group.items"
								:key="item[itemUniq]"
							>
								<slot name="item" :item="item"></slot>
							</div>
						</TransitionGroup>
					</template>
				</v-expansion-panel>
			</template>
		</v-expansion-panels>
	</div>
</template>
<script>
import { computed, ref } from 'vue'

export default {
	name: 'AoGroupedList',
	props: {
		items: {
			type: Array,
			default: () => [],
			validator: (value) => {
				return value.every((item) => {
					return item.hasOwnProperty('title') && item.hasOwnProperty('items')
				})
			}
		},
		itemUniq: {
			type: String,
			default: 'id',
		},
		groupUniq: {
			type: String,
			default: 'id',
		},
		groupTitle: {
			type: String,
			default: 'title',
		},
		groupAggregation: {
			type: String,
			default: 'aggregation',
		},
		groupActions: {
			type: Array,
			default: () => [],
			validator: (value) => {
				return value.every((item) => {
					return item.hasOwnProperty('name') && item.hasOwnProperty('title') && item.hasOwnProperty('icon')
				})
			}
		},
	},
	emits: [
		'action-click',
	],
	expose: [
		'expandGroup'
	],
	setup(props, { emit }) {
		const hasGroupActions = computed(() => props.groupActions?.length > 0 || props.groupActions === true)
		const expandedGroup = ref(null)
		const activeGroup = ref(null)

		const onGroupAction = (actionName, groupId) => {
			emit('action-click', {
				actionName,
				groupId
			})
		}

		const onMenuModelValueUpdate = (group, value) => {
			if (value) {
				activeGroup.value = group
			} else {
				activeGroup.value = null
			}

			console.log(activeGroup.value)
		}

		const expandGroup = (groupUniq) => {
			if (expandedGroup.value === groupUniq) return

			expandedGroup.value = null

			setTimeout(() => {
				expandedGroup.value = groupUniq
			}, 300)
		}

		return {
			hasGroupActions,
			expandedGroup,
			activeGroup,
			onGroupAction,
			expandGroup,
			onMenuModelValueUpdate,
		}
	}
}
</script>
<style lang="scss">
.ao-grouped-list {
	overflow-y: auto;

	& .v-expansion-panel-title {
		flex-direction: row-reverse !important;
		height: 22px !important;
		min-height: 22px !important;
		padding: 0px;
	}

	& .v-expansion-panel--active > .v-expansion-panel-title  {
		min-height: 22px !important;
		height: 22px !important;
	}

	& .v-expansion-panel-title__overlay {
		display: none;
	}

	& .v-expansion-panel-text__wrapper {
		padding: 4px 4px 4px 24px !important;
	}

	& .v-expansion-panels--variant-accordion > .v-expansion-panel:after {
		display: none;
	}

	& .v-expansion-panels {
		display: block !important;
	}

	& .v-expansion-panel__shadow {
		box-shadow: none !important;
	}

	& .v-expansion-panel-title__icon i {
		font-size: 14px !important;
	}

	&__group {
		position: relative;
		overflow: hidden;
	}

	&__group-actions {
		position: absolute;
		top: 50%;
		right: 4px;
		transform: translate3d(50px, -50%, 0);
		transition: transform 0.3s ease-in-out;
	}

	&__group:hover &__group-actions,
	&__group--active &__group-actions {
		transform: translate3d(0, -50%, 0);
	}

	&__group-aggregation {
		position: absolute;
		top: 50%;
		right: 4px;
		transform: translateY(-50%);
		transition: all 0.1s linear;
	}

	&__group:hover &__group-aggregation,
	&__group--active &__group-aggregation {
		transform: scale(0.1) translateY(-50%);
		opacity: 0;
	}
}

.list-move, /* apply transition to moving elements */
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leave-active {
  position: absolute;
}
</style>